// Payment documents (Stage 2E-4): the receipt (drawn from the database's frozen payment and invoice values, stored once with
// its SHA-256, verified on every open), the separate VOID-marked copy, verified proof / TDS-certificate files, and the client
// statement PDF (generated on demand from the database's rows; never stored). Never calculates a balance; never touches Accounts.
import { loadPayment, loadInvoice, loadStampImage, storeReceiptPdf, openVerifiedFile, clientStatement, clientOpenItems } from "./api";
import { pdfFileName } from "./format.js";
import { deliver } from "./quotationDocs";

// The PDF generators are loaded when a PDF is first made (a separate chunk; the screens themselves don't need jsPDF).
const receiptPdfBytes = async (p) => (await import("./receiptPdf.js")).receiptPdfBytes(p);
const statementPdfBytes = async (p) => (await import("./statementPdf.js")).statementPdfBytes(p);

// The receipt: re-read the payment and its invoice from the database (never from screen state).
export async function receiptBytes(paymentId, cal, mode = "issued") {
    const p = await loadPayment(paymentId);
    if (!p) throw new Error("Payment not found.");
    const [v, stamp] = await Promise.all([loadInvoice(p.invoice_id), loadStampImage()]);
    return { p, bytes: await receiptPdfBytes({ mode, payment: p, invoice: v, cal, stamp }) };
}
// "Create stored receipt" — right after recording, and later as the recovery when storing failed. Never replaces a stored one.
export async function createStoredReceipt(paymentId, cal) {
    const { p, bytes } = await receiptBytes(paymentId, cal, "issued");
    if (p.receipt_pdf_path) return p;
    return storeReceiptPdf(p, bytes);
}
export const storedReceiptBytes = async (p) => (await openVerifiedFile(p.receipt_pdf_path, p.receipt_pdf_sha256, "receipt")).bytes;
export const voidReceiptBytes = async (paymentId, cal) => (await receiptBytes(paymentId, cal, "void")).bytes;
export const receiptFileName = (p, suffix = "") => pdfFileName(p.receipt_no, suffix);

// A verified proof / certificate file, opened in a new tab or downloaded (its own type: PDF or image).
export async function openPaymentFile({ path, hash, what, action, win, name }) {
    const { bytes, type } = await openVerifiedFile(path, hash, what);
    if (type === "application/pdf" || /\.pdf$/i.test(path)) return deliver(bytes, { action, win, name: `${name}.pdf` });
    const url = URL.createObjectURL(new Blob([bytes], { type }));
    setTimeout(() => URL.revokeObjectURL(url), 120000);
    if (action === "download") {
        const a = document.createElement("a"); a.href = url; a.download = `${name}.${path.split(".").pop()}`; document.body.appendChild(a); a.click(); a.remove(); win?.close();
    } else if (win) win.location.href = url;
    else window.open(url, "_blank");
}

// Client statement PDF (D8): rows and open items from the database, laid out only; generated now, not stored.
export async function statementBytes({ client, settings, from, to, cal }) {
    const [rows, openItems] = await Promise.all([clientStatement(client.id, from, to), clientOpenItems(client.id, to)]);
    return statementPdfBytes({ issuer: settings || {}, client, rows, openItems, from, to, generatedAt: new Date().toISOString(), cal });
}
export const statementFileName = (from, to) => `Foxbeep-Statement-${from}-to-${to}.pdf`;
